import { type UseQueryResult, useQuery } from 'react-query';
import {
  AllowedRoleMappingService,
  PagedResultDto_AllowedRoleMappingGroupedVm,
  Wealthlane_CustomerService_AppServices_CustomerSuccess_AllowedRoleMappingsVm,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { TableParamsType } from '../../types/common';

// Type for flattened allowed role mapping with parent destination role info
export type FlattenedAllowedRoleMapping = Wealthlane_CustomerService_AppServices_CustomerSuccess_AllowedRoleMappingsVm & {
  destinationRoleId?: string;
  destinationRoleName?: string | null;
  destinationCompanyId?: string;
  destinationCompanyName?: string | null;
  mappings?: Wealthlane_CustomerService_AppServices_CustomerSuccess_AllowedRoleMappingsVm[];
};

export type FlattenedPagedResult = {
  items?: FlattenedAllowedRoleMapping[];
  totalCount?: number;
};

const useGetAllowedRoleMapping = (
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  tableInfo: TableParamsType
): UseQueryResult<FlattenedPagedResult | undefined, unknown> => {
  const { filter, sorting, skipCount, maxResultCount } = tableInfo;
  
  return useQuery(
    ['getAllowedRoleMappings', filter, sorting, skipCount, maxResultCount],
    async () => {
      const response = await handleRequest(
        AllowedRoleMappingService.getApiCustomerApiV2CustomerSuccessAllowedRoleMapping(
          '',
          '',
          filter,
          sorting,
          skipCount,
          maxResultCount
        )
      );

      if (!response || !response.items) {
        return response as FlattenedPagedResult;
      }

      // Flatten the grouped data
      const flattenedItems: FlattenedAllowedRoleMapping[] = [];
      
      response.items.forEach((group) => {
        if (group.mappings) {
          group.mappings.forEach((mapping) => {
            flattenedItems.push({
              ...mapping,
              destinationRoleId: group.destinationRoleId,
              destinationRoleName: group.destinationRoleName,
              destinationCompanyId: group.destinationCompanyId,
              destinationCompanyName: group.destinationCompanyName,
              mappings: group.mappings,
            });
          });
        }
      });
      return {
        items: flattenedItems,
        totalCount: response.totalCount,
      };
    }
  );
};

export default useGetAllowedRoleMapping;
